const MODEL_VIEWER_SRC =
  'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';

const ORBIT_PHI = 88;
const HOME_ORBIT_PHI = 82;
const HOME_ORBIT_PHI_MIN = 35;
const HOME_ORBIT_PHI_MAX = 125;
const HOME_ORBIT_RADIUS = '900%';
const HOME_RADIUS_MULTIPLIER = 3.4;
const HOME_FIELD_OF_VIEW = '48deg';
const HOME_INITIAL_THETA = 180;
const HOME_DRAG_SENSITIVITY = 0.42;
const PRODUCT_ORBIT_PHI = 90;
const PRODUCT_ORBIT_PHI_MOBILE = 88;
const PRODUCT_INITIAL_THETA = 180;
const ORBIT_RADIUS = '105%';
const PRODUCT_ORBIT_RADIUS = '105%';
const PRODUCT_ORBIT_RADIUS_MOBILE = '118%';
const PRODUCT_RADIUS_PADDING = 1.06;
const PRODUCT_RADIUS_PADDING_MOBILE = 1.14;
const PRODUCT_FIELD_OF_VIEW = '30deg';
const PRODUCT_FIELD_OF_VIEW_MOBILE = '34deg';
const MOBILE_PRODUCT_MEDIA_QUERY = '(max-width: 749px)';
const DRAG_SENSITIVITY = 0.32;
const FOLLOW_LERP = 0.28;
const MOMENTUM_FRICTION = 0.95;
const MOMENTUM_MIN = 0.025;
const HOME_ROTATE_BASE_DEG = 22;
const HEADER_ROTATE_BASE_DEG = 38;
const HEADER_ROTATE_FACE_FACTOR = 0.18;
const HEADER_ROTATE_EDGE_FACTOR = 1.45;
const HEADER_ROTATE_EDGE_CURVE = 0.68;
const LIGHTING_SHADOW_INTENSITY = 0;
const LIGHTING_SHADOW_SOFTNESS = 0;
const PRODUCT_SWAY_AMPLITUDE = 45;
const PRODUCT_SWAY_PERIOD_MS = 34000;
let modelViewerPromise = null;

function loadModelViewer() {
  if (customElements.get('model-viewer')) {
    return Promise.resolve();
  }

  if (modelViewerPromise) {
    return modelViewerPromise;
  }

  modelViewerPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = MODEL_VIEWER_SRC;
    script.fetchPriority = 'low';
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return modelViewerPromise;
}

/**
 * @param {HTMLElement} viewer
 */
function applyHeaderRenderQuality(viewer) {
  const dpr = window.devicePixelRatio || 1;
  const renderScale = Math.min(12, Math.max(4, Math.ceil(dpr * 4)));

  viewer.setAttribute('render-scale', String(renderScale));

  if ('renderScale' in viewer) {
    viewer.renderScale = renderScale;
  }
}

/**
 * Calidad adaptativa para el escenario home: nítido en desktop y móvil.
 * @param {HTMLElement} viewer
 */
function applyHomeRenderQuality(viewer) {
  const dpr = window.devicePixelRatio || 1;
  const mobile = window.matchMedia(MOBILE_PRODUCT_MEDIA_QUERY).matches;
  // Móvil: antes se quedaba en ~1–2 y el logo se veía blando; subimos cerca del escritorio.
  const renderScale = mobile
    ? Math.min(2.75, Math.max(1.75, Math.round(dpr * 1.55 * 10) / 10))
    : Math.min(3, Math.max(1.5, Math.round(dpr * 1.75 * 10) / 10));

  viewer.setAttribute('render-scale', String(renderScale));
  viewer.setAttribute('power-preference', 'high-performance');

  if ('renderScale' in viewer) {
    viewer.renderScale = renderScale;
  }
}

/**
 * @param {HTMLElement} viewer
 */
function applyProductRenderQuality(viewer) {
  const dpr = window.devicePixelRatio || 1;
  const renderScale = isMobileProductView()
    ? Math.min(2, Math.max(1, Math.ceil(dpr * 1.1)))
    : Math.min(4, Math.max(2, Math.ceil(dpr * 2)));

  viewer.setAttribute('render-scale', String(renderScale));

  if ('renderScale' in viewer) {
    viewer.renderScale = renderScale;
  }
}

/**
 * @returns {boolean}
 */
function isMobileProductView() {
  return window.matchMedia(MOBILE_PRODUCT_MEDIA_QUERY).matches;
}

/**
 * @param {HTMLElement | null | undefined} root
 */
function getOrbitPhiForRoot(root) {
  const variant = root?.dataset?.dlcLogoVariant;
  if (variant === 'product') {
    return isMobileProductView() ? PRODUCT_ORBIT_PHI_MOBILE : PRODUCT_ORBIT_PHI;
  }
  if (variant === 'home') return HOME_ORBIT_PHI;

  return ORBIT_PHI;
}

/**
 * @param {HTMLElement | null | undefined} root
 */
function getOrbitRadiusForRoot(root) {
  const variant = root?.dataset?.dlcLogoVariant;
  if (variant === 'product') {
    return isMobileProductView() ? PRODUCT_ORBIT_RADIUS_MOBILE : PRODUCT_ORBIT_RADIUS;
  }
  if (variant === 'home') {
    const framed = homeFramingState.get(
      /** @type {HTMLElement} */ (root?.querySelector?.('model-viewer'))
    );
    if (framed?.radius) return framed.radius;
    return HOME_ORBIT_RADIUS;
  }

  return ORBIT_RADIUS;
}

/** @type {WeakMap<HTMLElement, { phi: number, radius: string, target: string }>} */
const homeFramingState = new WeakMap();

/**
 * Aleja el logo en home midiendo el bounding box real (los % a veces no bastan).
 * @param {HTMLElement} viewer
 * @param {HTMLElement | null | undefined} root
 */
function applyHomeFraming(viewer, root) {
  const orientation = root?.dataset?.modelOrientation || '0deg 0deg 0deg';
  const orbitPhiDeg = HOME_ORBIT_PHI;

  viewer.setAttribute('orientation', orientation);
  viewer.setAttribute('camera-target', 'auto auto auto');
  viewer.setAttribute('field-of-view', HOME_FIELD_OF_VIEW);
  viewer.setAttribute('min-camera-orbit', `auto ${HOME_ORBIT_PHI_MIN}deg auto`);
  viewer.setAttribute('max-camera-orbit', `auto ${HOME_ORBIT_PHI_MAX}deg auto`);
  viewer.setAttribute('shadow-intensity', String(LIGHTING_SHADOW_INTENSITY));
  viewer.setAttribute('shadow-softness', String(LIGHTING_SHADOW_SOFTNESS));

  if ('orientation' in viewer) {
    viewer.orientation = orientation;
  }

  applyHomeSkybox(viewer, root);

  const finalizeFraming = () => {
    let radius = HOME_ORBIT_RADIUS;
    let target = 'auto auto auto';

    if (typeof viewer.getCameraOrbit === 'function') {
      try {
        if (typeof viewer.updateFraming === 'function') {
          viewer.updateFraming();
        }
      } catch (_) {
        // ignore framing race
      }

      const orbit = viewer.getCameraOrbit();
      const radiusM = (orbit.radius * HOME_RADIUS_MULTIPLIER).toFixed(4);
      radius = `${radiusM}m`;

      const phiRad = (orbitPhiDeg * Math.PI) / 180;
      const thetaRad = (HOME_INITIAL_THETA * Math.PI) / 180;
      viewer.cameraOrbit = `${thetaRad}rad ${phiRad}rad ${radiusM}m`;
      viewer.setAttribute('camera-orbit', `${HOME_INITIAL_THETA}deg ${orbitPhiDeg}deg ${radius}`);
      viewer.setAttribute('min-camera-orbit', `auto ${HOME_ORBIT_PHI_MIN}deg ${radius}`);
      viewer.setAttribute('max-camera-orbit', `auto ${HOME_ORBIT_PHI_MAX}deg ${radius}`);
    } else {
      viewer.setAttribute(
        'camera-orbit',
        `${HOME_INITIAL_THETA}deg ${orbitPhiDeg}deg ${radius}`
      );
    }

    if (typeof viewer.getCameraTarget === 'function') {
      const cameraTarget = viewer.getCameraTarget();
      target = `${cameraTarget.x.toFixed(4)}m ${cameraTarget.y.toFixed(4)}m ${cameraTarget.z.toFixed(4)}m`;
      viewer.cameraTarget = target;
      viewer.setAttribute('camera-target', target);
    }

    homeFramingState.set(viewer, {
      phi: orbitPhiDeg,
      radius,
      target,
    });

    applyHomeRenderQuality(viewer);
    productOrbitAppliers.get(viewer)?.();
  };

  if (typeof viewer.updateFraming === 'function') {
    viewer.updateFraming();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(finalizeFraming);
    });
    return;
  }

  finalizeFraming();
}

/**
 * @param {HTMLElement} viewer
 * @param {HTMLElement | null | undefined} root
 */
function getHomeFramedOrbit(viewer, root) {
  const framed = homeFramingState.get(viewer);
  if (framed) return framed;

  return {
    phi: HOME_ORBIT_PHI,
    radius: getOrbitRadiusForRoot(root),
    target: 'auto auto auto',
  };
}

/**
 * @param {HTMLElement} viewer
 * @param {HTMLElement | null | undefined} root
 */
function applyHomeSkybox(viewer, root) {
  const skybox = root?.dataset?.skyboxImage;
  if (!skybox) {
    viewer.removeAttribute('skybox-image');
    viewer.setAttribute('environment-image', 'neutral');
    viewer.removeAttribute('exposure');
    viewer.setAttribute('min-camera-orbit', `auto ${HOME_ORBIT_PHI}deg auto`);
    viewer.setAttribute('max-camera-orbit', `auto ${HOME_ORBIT_PHI}deg auto`);
    viewer.removeAttribute('field-of-view');

    if ('environmentImage' in viewer) {
      viewer.environmentImage = 'neutral';
    }
    if ('skyboxImage' in viewer) {
      viewer.skyboxImage = null;
    }
    return;
  }

  const exposure = Number(root?.dataset?.skyboxExposure || 1);

  viewer.setAttribute('skybox-image', skybox);
  viewer.setAttribute('environment-image', skybox);
  viewer.setAttribute('exposure', String(Number.isFinite(exposure) ? exposure : 1));
  viewer.setAttribute('field-of-view', HOME_FIELD_OF_VIEW);
  viewer.setAttribute('min-camera-orbit', `auto ${HOME_ORBIT_PHI_MIN}deg auto`);
  viewer.setAttribute('max-camera-orbit', `auto ${HOME_ORBIT_PHI_MAX}deg auto`);

  if ('skyboxImage' in viewer) {
    viewer.skyboxImage = skybox;
  }
  if ('environmentImage' in viewer) {
    viewer.environmentImage = skybox;
  }
  if ('exposure' in viewer) {
    viewer.exposure = Number.isFinite(exposure) ? exposure : 1;
  }
}

/**
 * @param {HTMLElement | null | undefined} root
 */
function getInitialThetaForRoot(root) {
  if (root?.dataset?.dlcLogoVariant === 'product') return PRODUCT_INITIAL_THETA;
  if (root?.dataset?.dlcLogoVariant === 'home') return HOME_INITIAL_THETA;

  return 0;
}

/**
 * Pendulo suave: media rotacion hacia un lado, vuelta al centro, media al otro y vuelta.
 * @param {number} elapsedMs
 */
function getProductSwayOffset(elapsedMs) {
  const phase = (elapsedMs % PRODUCT_SWAY_PERIOD_MS) / PRODUCT_SWAY_PERIOD_MS;
  const amplitude = PRODUCT_SWAY_AMPLITUDE;

  if (phase < 0.25) {
    return amplitude * (phase / 0.25);
  }

  if (phase < 0.5) {
    return amplitude * (1 - (phase - 0.25) / 0.25);
  }

  if (phase < 0.75) {
    return -amplitude * ((phase - 0.5) / 0.25);
  }

  return -amplitude * (1 - (phase - 0.75) / 0.25);
}

/**
 * @param {HTMLElement | null | undefined} root
 */
function getFieldOfViewForRoot(root) {
  if (root?.dataset?.dlcLogoVariant !== 'product') return null;

  return isMobileProductView() ? PRODUCT_FIELD_OF_VIEW_MOBILE : PRODUCT_FIELD_OF_VIEW;
}

/** @type {WeakMap<HTMLElement, { phi: number, radius: string, target: string }>} */
const productFramingState = new WeakMap();

/** @type {WeakMap<HTMLElement, () => void>} */
const productOrbitAppliers = new WeakMap();

/**
 * @param {HTMLElement} viewer
 * @param {HTMLElement | null | undefined} root
 */
function applyProductFraming(viewer, root) {
  const orientation = root?.dataset?.modelOrientation || '0deg 0deg 0deg';
  const orbitPhiDeg = getOrbitPhiForRoot(root);
  const initialThetaDeg = getInitialThetaForRoot(root);
  const radiusPadding = isMobileProductView()
    ? PRODUCT_RADIUS_PADDING_MOBILE
    : PRODUCT_RADIUS_PADDING;
  const fieldOfView = getFieldOfViewForRoot(root);

  viewer.setAttribute('orientation', orientation);
  viewer.setAttribute('environment-image', 'neutral');
  viewer.setAttribute('camera-target', 'auto auto auto');
  viewer.setAttribute('shadow-intensity', String(LIGHTING_SHADOW_INTENSITY));
  viewer.setAttribute('shadow-softness', String(LIGHTING_SHADOW_SOFTNESS));
  viewer.setAttribute('auto-rotate-delay', '0');
  viewer.removeAttribute('exposure');
  viewer.removeAttribute('tone-mapping');

  if (fieldOfView) {
    viewer.setAttribute('field-of-view', fieldOfView);
  }

  if ('orientation' in viewer) {
    viewer.orientation = orientation;
  }

  if ('environmentImage' in viewer) {
    viewer.environmentImage = 'neutral';
  }

  if ('shadowIntensity' in viewer) {
    viewer.shadowIntensity = LIGHTING_SHADOW_INTENSITY;
  }

  if ('shadowSoftness' in viewer) {
    viewer.shadowSoftness = LIGHTING_SHADOW_SOFTNESS;
  }

  const finalizeFraming = () => {
    let radius = getOrbitRadiusForRoot(root);
    let target = 'auto auto auto';

    if (typeof viewer.getCameraOrbit === 'function') {
      const orbit = viewer.getCameraOrbit();
      const radiusM = (orbit.radius * radiusPadding).toFixed(4);
      radius = `${radiusM}m`;

      const thetaRad = (initialThetaDeg * Math.PI) / 180;
      const phiRad = (orbitPhiDeg * Math.PI) / 180;
      viewer.cameraOrbit = `${thetaRad}rad ${phiRad}rad ${radiusM}m`;
    } else {
      viewer.setAttribute(
        'camera-orbit',
        `${initialThetaDeg}deg ${orbitPhiDeg}deg ${radius}`
      );
    }

    if (typeof viewer.getCameraTarget === 'function') {
      const cameraTarget = viewer.getCameraTarget();
      target = `${cameraTarget.x.toFixed(4)}m ${cameraTarget.y.toFixed(4)}m ${cameraTarget.z.toFixed(4)}m`;
      viewer.cameraTarget = target;
    }

    viewer.setAttribute('min-camera-orbit', `auto ${orbitPhiDeg}deg auto`);
    viewer.setAttribute('max-camera-orbit', `auto ${orbitPhiDeg}deg auto`);

    productFramingState.set(viewer, {
      phi: orbitPhiDeg,
      radius,
      target,
    });

    applyProductRenderQuality(viewer);
    productOrbitAppliers.get(viewer)?.();
  };

  if (typeof viewer.updateFraming === 'function') {
    viewer.updateFraming();
    window.requestAnimationFrame(finalizeFraming);
    return;
  }

  finalizeFraming();
}

/**
 * @param {HTMLElement} viewer
 * @param {HTMLElement | null | undefined} root
 */
function getProductFramedOrbit(viewer, root) {
  const framed = productFramingState.get(viewer);
  if (framed) return framed;

  return {
    phi: getOrbitPhiForRoot(root),
    radius: getOrbitRadiusForRoot(root),
    target: 'auto auto auto',
  };
}

/**
 * @param {HTMLElement} viewer
 */
function applyViewerSetup(viewer) {
  const root = viewer.closest('[data-dlc-logo-3d], .dlc-home-logo-3d');
  const variant =
    root?.dataset?.dlcLogoVariant || (root?.classList.contains('dlc-home-logo-3d') ? 'home' : 'header');
  const orientation =
    root?.dataset?.modelOrientation || '0deg 0deg 0deg';

  const tune = () => {
    if (variant === 'product') {
      applyProductFraming(viewer, root);
      return;
    }

    if (variant === 'home') {
      applyHomeFraming(viewer, root);
      return;
    }

    const orbitPhi = getOrbitPhiForRoot(root);
    const orbitRadius = getOrbitRadiusForRoot(root);
    viewer.setAttribute('orientation', orientation);
    viewer.setAttribute('camera-orbit', `0deg ${orbitPhi}deg ${orbitRadius}`);
    viewer.setAttribute('shadow-intensity', String(LIGHTING_SHADOW_INTENSITY));
    viewer.setAttribute('shadow-softness', String(LIGHTING_SHADOW_SOFTNESS));
    viewer.setAttribute('auto-rotate-delay', '0');
    viewer.removeAttribute('tone-mapping');
    viewer.setAttribute('environment-image', 'neutral');
    viewer.setAttribute('min-camera-orbit', `auto ${orbitPhi}deg auto`);
    viewer.setAttribute('max-camera-orbit', `auto ${orbitPhi}deg auto`);
    viewer.removeAttribute('exposure');
    viewer.removeAttribute('skybox-image');

    if ('environmentImage' in viewer) {
      viewer.environmentImage = 'neutral';
    }

    if ('shadowIntensity' in viewer) {
      viewer.shadowIntensity = LIGHTING_SHADOW_INTENSITY;
    }

    if ('shadowSoftness' in viewer) {
      viewer.shadowSoftness = LIGHTING_SHADOW_SOFTNESS;
    }

    if ('orientation' in viewer) {
      viewer.orientation = orientation;
    }

    applyHeaderRenderQuality(viewer);
  };

  if (viewer.loaded) {
    tune();
    return;
  }

  if (variant === 'home' && root?.dataset?.skyboxImage) {
    applyHomeSkybox(viewer, root);
  } else {
    viewer.setAttribute('environment-image', 'neutral');
  }
  viewer.addEventListener('load', tune, { once: true });
}

/**
 * @param {number} angleDeg
 */
function getIrregularRotateSpeedFactor(angleDeg) {
  const normalized = ((angleDeg % 360) + 360) % 360;
  const edge = Math.abs(Math.sin((normalized * Math.PI) / 180));
  const edgeShaped = Math.pow(edge, HEADER_ROTATE_EDGE_CURVE);

  return (
    HEADER_ROTATE_FACE_FACTOR +
    (HEADER_ROTATE_EDGE_FACTOR - HEADER_ROTATE_FACE_FACTOR) * edgeShaped
  );
}

/**
 * @param {HTMLElement} viewer
 * @param {{ isDragging: boolean }} orbitState
 * @param {HTMLElement} root
 * @param {{ autoRotate?: boolean, gentleSway?: boolean, slowRotate?: boolean }} [options]
 */
function bindHorizontalOrbit(viewer, orbitState, root, options = {}) {
  const autoRotate = options.autoRotate !== false;
  const gentleSway = options.gentleSway === true;
  const slowRotate = options.slowRotate === true;
  const allowSkyLook =
    root?.dataset?.dlcLogoVariant === 'home' && Boolean(root?.dataset?.skyboxImage);
  const initialTheta = autoRotate ? 0 : getInitialThetaForRoot(root);
  // Home: la vista empieza a 180° para leer el logo correctamente
  const homeStartTheta =
    root?.dataset?.dlcLogoVariant === 'home' ? HOME_INITIAL_THETA : initialTheta;
  const startTheta = root?.dataset?.dlcLogoVariant === 'home' ? homeStartTheta : initialTheta;
  const initialPhi = getOrbitPhiForRoot(root);
  let currentTheta = startTheta;
  let targetTheta = startTheta;
  let currentPhi = initialPhi;
  let targetPhi = initialPhi;
  let velocity = 0;
  let isDragging = false;
  let lastX = 0;
  let lastY = 0;
  let rafId = 0;
  let lastTime = 0;
  let shouldAutoRotate = false;
  let shouldSway = false;
  let swayStartTime = performance.now();

  viewer.removeAttribute('auto-rotate');
  viewer.setAttribute('rotation-per-second', '0deg');

  const stopLoop = () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };

  const clampPhi = (phi) =>
    Math.min(HOME_ORBIT_PHI_MAX, Math.max(HOME_ORBIT_PHI_MIN, phi));

  const applyOrbit = () => {
    if (root?.dataset?.dlcLogoVariant === 'product') {
      const framed = getProductFramedOrbit(viewer, root);
      viewer.setAttribute(
        'camera-orbit',
        `${currentTheta.toFixed(2)}deg ${framed.phi}deg ${framed.radius}`
      );
      viewer.setAttribute('camera-target', framed.target);
      return;
    }

    if (root?.dataset?.dlcLogoVariant === 'home') {
      const framed = getHomeFramedOrbit(viewer, root);
      const phi = allowSkyLook ? currentPhi : framed.phi;
      viewer.setAttribute(
        'camera-orbit',
        `${currentTheta.toFixed(2)}deg ${phi.toFixed(2)}deg ${framed.radius}`
      );
      if (framed.target) {
        viewer.setAttribute('camera-target', framed.target);
      }
      return;
    }

    viewer.setAttribute(
      'camera-orbit',
      `${currentTheta.toFixed(2)}deg ${getOrbitPhiForRoot(root)}deg ${getOrbitRadiusForRoot(root)}`
    );
  };

  const tick = (now) => {
    if (isDragging) {
      currentTheta += (targetTheta - currentTheta) * FOLLOW_LERP;
      if (allowSkyLook) {
        currentPhi += (targetPhi - currentPhi) * FOLLOW_LERP;
      }
      lastTime = 0;
    } else if (Math.abs(velocity) > MOMENTUM_MIN) {
      currentTheta += velocity;
      velocity *= MOMENTUM_FRICTION;
      targetTheta = currentTheta;
      lastTime = 0;
    } else if (shouldAutoRotate && !document.hidden) {
      if (!lastTime) {
        lastTime = now;
      }

      const delta = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      if (slowRotate) {
        currentTheta = (currentTheta + PRODUCT_AUTO_ROTATE_DEG * delta) % 360;
      } else {
        currentTheta =
          (currentTheta +
            HOME_ROTATE_BASE_DEG * getIrregularRotateSpeedFactor(currentTheta) * delta) %
          360;
      }

      targetTheta = currentTheta;
    } else if (gentleSway && shouldSway && !document.hidden && !orbitState.isDragging) {
      currentTheta = initialTheta + getProductSwayOffset(now - swayStartTime);
      targetTheta = currentTheta;
      lastTime = 0;
    } else {
      rafId = 0;
      lastTime = 0;
      applyOrbit();
      return;
    }

    applyOrbit();
    rafId = window.requestAnimationFrame(tick);
  };

  const startLoop = () => {
    if (!rafId) {
      lastTime = 0;
      rafId = window.requestAnimationFrame(tick);
    }
  };

  const pauseAutoRotate = () => {
    viewer.setAttribute('rotation-per-second', '0deg');
  };

  const resumeMotion = () => {
    if (autoRotate && !document.hidden && !orbitState.isDragging && shouldAutoRotate) {
      startLoop();
      return;
    }

    if (gentleSway && !document.hidden && !orbitState.isDragging && shouldSway) {
      startLoop();
    }
  };

  if ((autoRotate || gentleSway) && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        shouldAutoRotate = autoRotate && isVisible;
        shouldSway = gentleSway && isVisible;

        if ((shouldAutoRotate || shouldSway) && !orbitState.isDragging && !document.hidden) {
          startLoop();
        } else if (!isDragging) {
          stopLoop();
        }
      },
      { root: null, threshold: 0.05 }
    );

    observer.observe(root);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopLoop();
        return;
      }

      resumeMotion();
    });
  }

  viewer.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      isDragging = true;
      orbitState.isDragging = true;
      lastX = event.clientX;
      lastY = event.clientY;
      velocity = 0;
      pauseAutoRotate();

      if (viewer.setPointerCapture) {
        viewer.setPointerCapture(event.pointerId);
      }

      startLoop();
    },
    { passive: true }
  );

  viewer.addEventListener(
    'pointermove',
    (event) => {
      if (!isDragging) return;

      const deltaX = event.clientX - lastX;
      const deltaY = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;

      const sensitivity = allowSkyLook ? HOME_DRAG_SENSITIVITY : DRAG_SENSITIVITY;
      targetTheta -= deltaX * sensitivity;

      if (allowSkyLook) {
        // Escena estática: sin inercia; solo se mueve mientras deslizas
        velocity = 0;
        targetPhi = clampPhi(targetPhi - deltaY * sensitivity * 0.65);
      } else {
        velocity = -deltaX * sensitivity * 0.12;
      }

      event.preventDefault();
    },
    { passive: false }
  );

  /** @param {PointerEvent} event */
  const endDrag = (event) => {
    if (!isDragging) return;

    isDragging = false;
    orbitState.isDragging = false;
    velocity = 0;

    // Congela el frame actual al soltar (mapa estático)
    currentTheta = targetTheta;
    if (allowSkyLook) {
      currentPhi = targetPhi;
    }
    applyOrbit();
    stopLoop();

    if (viewer.hasPointerCapture?.(event.pointerId)) {
      viewer.releasePointerCapture(event.pointerId);
    }

    if (!allowSkyLook) {
      resumeMotion();
    }
  };

  viewer.addEventListener('pointerup', endDrag);
  viewer.addEventListener('pointercancel', endDrag);

  productOrbitAppliers.set(viewer, applyOrbit);
  applyOrbit();

  if (gentleSway) {
    if (!('IntersectionObserver' in window)) {
      shouldSway = true;
      startLoop();
    }
  }
}

/**
 * @param {HTMLElement} viewer
 * @param {HTMLElement} root
 */
function bindHeaderIrregularRotate(viewer, root) {
  let theta = 0;
  let rafId = 0;
  let lastTime = 0;
  let isActive = false;
  let shouldRun = false;

  viewer.removeAttribute('auto-rotate');
  viewer.setAttribute('rotation-per-second', '0deg');

  const applyOrbit = () => {
    viewer.setAttribute(
      'camera-orbit',
      `${theta.toFixed(2)}deg ${ORBIT_PHI}deg ${ORBIT_RADIUS}`
    );
  };

  const getSpeedFactor = getIrregularRotateSpeedFactor;

  const tick = (now) => {
    if (!isActive || document.hidden) {
      rafId = 0;
      lastTime = 0;
      return;
    }

    if (!lastTime) {
      lastTime = now;
    }

    const delta = Math.min((now - lastTime) / 1000, 0.05);
    lastTime = now;
    theta = (theta + HEADER_ROTATE_BASE_DEG * getSpeedFactor(theta) * delta) % 360;
    applyOrbit();

    rafId = window.requestAnimationFrame(tick);
  };

  const start = () => {
    isActive = true;

    if (!rafId) {
      lastTime = 0;
      rafId = window.requestAnimationFrame(tick);
    }
  };

  const stop = () => {
    isActive = false;

    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
      lastTime = 0;
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      shouldRun = entries.some((entry) => entry.isIntersecting);
      if (shouldRun && !document.hidden) {
        start();
      } else {
        stop();
      }
    },
    { root: null, threshold: 0.05 }
  );

  observer.observe(root);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stop();
      return;
    }

    if (shouldRun) {
      start();
    }
  });
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} viewer
 */
function bindLoadingState(root, viewer) {
  const finishLoading = () => {
    root.classList.remove('dlc-home-logo-3d--loading');
    root.classList.remove('dlc-logo-3d--loading');
    root.classList.remove('dlc-logo-3d--awaiting-model');
  };

  if (viewer.loaded) {
    finishLoading();
    return;
  }

  viewer.addEventListener('load', finishLoading, { once: true });
  viewer.addEventListener('error', finishLoading, { once: true });
}

/**
 * @param {HTMLElement} card
 */
function bindProductCardFavorite(card) {
  const button = card.querySelector('[data-favorite-toggle]');
  const productId = card.dataset.productId;

  if (!(button instanceof HTMLButtonElement) || !productId) return;

  const storageKey = `dlc-favorite-${productId}`;

  const syncState = () => {
    const isActive = window.localStorage.getItem(storageKey) === '1';
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  };

  syncState();

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    const isActive = window.localStorage.getItem(storageKey) === '1';
    window.localStorage.setItem(storageKey, isActive ? '0' : '1');
    syncState();
  });
}

/**
 * @param {HTMLElement} card
 */
function bindProductCardReveal(card) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    card.classList.add('is-visible');
    return;
  }

  if (!('IntersectionObserver' in window)) {
    card.classList.add('is-visible');
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        card.classList.add('is-visible');
        observer.unobserve(card);
      });
    },
    { threshold: 0.15 }
  );

  observer.observe(card);
}

/**
 * @param {HTMLElement} panel
 * @param {{ isDragging: boolean }} orbitState
 */
function bindProductNavigation(panel, orbitState) {
  const url = panel.dataset.productUrl;
  if (!url) return;

  /** @type {{ x: number, y: number } | null} */
  let pointerStart = null;

  panel.addEventListener(
    'pointerdown',
    (event) => {
      pointerStart = { x: event.clientX, y: event.clientY };
    },
    { passive: true }
  );

  panel.addEventListener('click', (event) => {
    if (orbitState.isDragging) {
      event.preventDefault();
      return;
    }

    if (pointerStart) {
      const deltaX = Math.abs(event.clientX - pointerStart.x);
      const deltaY = Math.abs(event.clientY - pointerStart.y);

      if (deltaX > 8 || deltaY > 8) {
        event.preventDefault();
        return;
      }
    }

    window.location.href = url;
  });

  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    window.location.href = url;
  });
}

/**
 * @param {HTMLElement} root
 */
async function bindLogoRoot(root) {
  const viewer = root.querySelector('model-viewer');
  const variant =
    root.dataset.dlcLogoVariant || (root.classList.contains('dlc-home-logo-3d') ? 'home' : 'header');
  const orbitState = { isDragging: false };

  // Home: primero model-viewer (siempre visible), luego intentamos escena Three encima
  if (variant === 'home' && viewer) {
    bindLoadingState(root, viewer);
    applyViewerSetup(viewer);
    bindHorizontalOrbit(viewer, orbitState, root, {
      autoRotate: false,
      gentleSway: false,
      slowRotate: false,
    });

    if (root.hasAttribute('data-dlc-home-scene') && root.dataset.modelSrc) {
      try {
        const sceneModuleUrl =
          window.DLC_HOME_SCENE_URL || new URL('dlc-home-scene.js', import.meta.url).href;
        const { bootHomeScene } = await import(sceneModuleUrl);
        await bootHomeScene(root);
      } catch (err) {
        console.warn('[dlc-home] scene enhance skipped', err);
        viewer.hidden = false;
        viewer.style.removeProperty('display');
      }
    }
    return;
  }

  if (!viewer) {
    root.classList.remove('dlc-home-logo-3d--loading');
    return;
  }

  bindLoadingState(root, viewer);
  applyViewerSetup(viewer);

  if (variant === 'header') {
    if (!('IntersectionObserver' in window)) return;

    bindHeaderIrregularRotate(viewer, root);
    return;
  }

  if (variant === 'product') {
    const productCard = root.closest('[data-dlc-product-card]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoRotateEnabled =
      productCard instanceof HTMLElement &&
      productCard.dataset.autoRotate !== 'false' &&
      !reducedMotion;

    bindHorizontalOrbit(viewer, orbitState, root, {
      autoRotate: false,
      gentleSway: autoRotateEnabled,
      slowRotate: false,
    });

    if (productCard instanceof HTMLElement) {
      bindProductCardFavorite(productCard);
      return;
    }

    const productPanel = root.closest('[data-product-url]');

    if (productPanel instanceof HTMLElement) {
      bindProductNavigation(productPanel, orbitState);
    }

    return;
  }

  bindHorizontalOrbit(viewer, orbitState, root, {
    autoRotate: false,
    gentleSway: false,
    slowRotate: false,
  });
}

function bindProductCameraResize() {
  if (bindProductCameraResize.initialized) return;

  bindProductCameraResize.initialized = true;

  let resizeTimer = 0;

  window.addEventListener(
    'resize',
    () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        document.querySelectorAll('[data-dlc-logo-variant="product"] model-viewer').forEach((viewer) => {
          if (viewer instanceof HTMLElement) {
            applyViewerSetup(viewer);
          }
        });
      }, 120);
    },
    { passive: true }
  );
}

/**
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function isDeferredProductRoot(root) {
  const viewer = root.querySelector('model-viewer');
  return Boolean(
    viewer?.hasAttribute('data-model-src') &&
      root.dataset?.dlcLogoVariant === 'product' &&
      !viewer.getAttribute('src')
  );
}

/**
 * @param {HTMLElement} root
 */
function activateDeferredProductRoot(root) {
  const viewer = root.querySelector('model-viewer');
  const src = viewer?.getAttribute('data-model-src');

  if (!(viewer instanceof HTMLElement) || !src || viewer.getAttribute('src')) {
    return false;
  }

  root.classList.add('dlc-logo-3d--loading');
  viewer.setAttribute('src', src);
  return true;
}

/**
 * @param {HTMLElement[]} deferredRoots
 * @param {(roots: HTMLElement[]) => void} bootRoots
 */
function observeDeferredProductModels(deferredRoots, bootRoots) {
  /** @type {Set<HTMLElement>} */
  const pending = new Set(deferredRoots);

  /**
   * @param {HTMLElement} root
   */
  const activate = (root) => {
    if (!pending.has(root)) return;

    pending.delete(root);
    activateDeferredProductRoot(root);
    bootRoots([root]);
  };

  if (!('IntersectionObserver' in window)) {
    deferredRoots.forEach(activate);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        activate(/** @type {HTMLElement} */ (entry.target));
        observer.unobserve(entry.target);
      });
    },
    { root: null, rootMargin: '320px 0px', threshold: 0.01 }
  );

  deferredRoots.forEach((root) => observer.observe(root));
}

/**
 * Empuje suave al pasar el cursor: el cubo cede en el punto de contacto
 * y sigue flotando, como un objeto en gravedad cero.
 */
function bindHomePortalNudge() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const portals = Array.from(document.querySelectorAll('.dlc-home-portal'));
  if (!portals.length) return;

  const MAX_RX = 12;
  const MAX_RY = 18;
  const MAX_TX = 11;
  const MAX_TY = 8;
  const MAX_TZ = 16;
  const FOLLOW = 0.09;
  const REST = 0.045;
  const IDLE = 0.02;

  const states = portals.map((el) => ({
    el,
    hover: false,
    rx: 0,
    ry: 0,
    tx: 0,
    ty: 0,
    tz: 0,
    tRx: 0,
    tRy: 0,
    tTx: 0,
    tTy: 0,
    tTz: 0,
  }));

  let raf = 0;

  /**
   * @param {(typeof states)[number]} state
   * @param {PointerEvent} event
   */
  const setTarget = (state, event) => {
    const rect = state.el.getBoundingClientRect();
    const nx = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1));
    const ny = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1));
    const reach = Math.min(1, Math.hypot(nx, ny));

    state.tRx = -ny * MAX_RX;
    state.tRy = nx * MAX_RY;
    state.tTx = nx * MAX_TX;
    state.tTy = ny * MAX_TY;
    state.tTz = -MAX_TZ * (0.35 + 0.65 * reach);
  };

  const apply = (state) => {
    const { el } = state;
    el.style.setProperty('--press-rx', `${state.rx.toFixed(3)}deg`);
    el.style.setProperty('--press-ry', `${state.ry.toFixed(3)}deg`);
    // Versión sin unidad para desplazar el reflejo del cristal
    el.style.setProperty('--press-rxn', state.rx.toFixed(3));
    el.style.setProperty('--press-ryn', state.ry.toFixed(3));
    el.style.setProperty('--press-tx', `${state.tx.toFixed(2)}px`);
    el.style.setProperty('--press-ty', `${state.ty.toFixed(2)}px`);
    el.style.setProperty('--press-tz', `${state.tz.toFixed(2)}px`);
  };

  const tick = () => {
    let active = false;

    for (const state of states) {
      const k = state.hover ? FOLLOW : REST;
      state.rx += (state.tRx - state.rx) * k;
      state.ry += (state.tRy - state.ry) * k;
      state.tx += (state.tTx - state.tx) * k;
      state.ty += (state.tTy - state.ty) * k;
      state.tz += (state.tTz - state.tz) * k;
      apply(state);

      if (
        state.hover ||
        Math.abs(state.rx) + Math.abs(state.ry) + Math.abs(state.tx) + Math.abs(state.ty) + Math.abs(state.tz) >
          IDLE
      ) {
        active = true;
      }
    }

    raf = active ? requestAnimationFrame(tick) : 0;
  };

  const ensureTick = () => {
    if (!raf) raf = requestAnimationFrame(tick);
  };

  states.forEach((state) => {
    state.el.addEventListener('pointerenter', (event) => {
      state.hover = true;
      setTarget(state, event);
      ensureTick();
    });
    state.el.addEventListener('pointermove', (event) => {
      if (!state.hover) return;
      setTarget(state, event);
      ensureTick();
    });
    state.el.addEventListener('pointerleave', () => {
      state.hover = false;
      state.tRx = 0;
      state.tRy = 0;
      state.tTx = 0;
      state.tTy = 0;
      state.tTz = 0;
      ensureTick();
    });
  });
}

function initLogo3d() {
  bindHomePortalNudge();
  const roots = Array.from(document.querySelectorAll('.dlc-home-logo-3d, [data-dlc-logo-3d]'));
  const cards = Array.from(document.querySelectorAll('[data-dlc-product-card]'));
  /** @type {HTMLElement[]} */
  const immediateRoots = [];
  /** @type {HTMLElement[]} */
  const deferredRoots = [];

  roots.forEach((root) => {
    if (!(root instanceof HTMLElement) || !root.querySelector('model-viewer')) return;

    if (isDeferredProductRoot(root)) {
      deferredRoots.push(root);
      return;
    }

    immediateRoots.push(root);
  });

  cards.forEach((card) => {
    if (card instanceof HTMLElement) {
      bindProductCardReveal(card);
    }
  });

  /**
   * @param {HTMLElement[]} rootsToBoot
   */
  const bootRoots = (rootsToBoot) => {
    if (!rootsToBoot.length) return;

    loadModelViewer()
      .then(() => {
        bindProductCameraResize();
        rootsToBoot.forEach((root) => {
          bindLogoRoot(root);
        });
      })
      .catch(() => {
        rootsToBoot.forEach((root) => {
          root.classList.remove('dlc-home-logo-3d--loading');
          root.classList.remove('dlc-logo-3d--loading');
          root.classList.remove('dlc-logo-3d--awaiting-model');
          // Aun así intentamos escena Three en home
          if (root.dataset?.dlcLogoVariant === 'home') {
            bindLogoRoot(root);
          }
        });
      });
  };

  const scheduleBoot = () => {
    if (immediateRoots.length) {
      bootRoots(immediateRoots);
    }

    if (deferredRoots.length) {
      observeDeferredProductModels(deferredRoots, bootRoots);
    }
  };

  if (!immediateRoots.length && !deferredRoots.length) return;

  const hasHomeScene = immediateRoots.some((root) => root.dataset?.dlcLogoVariant === 'home');

  // En home el 3D es el hero: arrancar ya. En el resto, diferir.
  if (hasHomeScene) {
    scheduleBoot();
    return;
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(scheduleBoot, { timeout: 2000 });
  } else {
    window.setTimeout(scheduleBoot, 200);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLogo3d);
} else {
  initLogo3d();
}
